/**
 * @fileoverview Clever Instant Login ("Log in with Clever") — AR-925
 * @description Browser side of the OAuth authorization-code flow. The sign-in
 * button sends the browser to Clever; Clever returns to
 * `/account/clever/callback` with `?code=`; the callback page posts the code
 * to the backend, which exchanges it for a user token, resolves the
 * roster-synced user and opens the Devise session. Logins started from the
 * Clever portal land on the callback page directly, with `code` only.
 *
 * The `redirect_uri` sent to Clever and the one posted to the backend must be
 * byte-identical, and both must appear in the Clever dashboard and in the
 * backend `CLEVER_REDIRECT_URIS` allowlist — so every caller derives it from
 * `cleverRedirectUri()`.
 */

import axios from 'axios'
import Cookies from 'js-cookie'

import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import { IUser } from 'context/interface'
import env from '../env.json'

export const CLEVER_AUTHORIZE_URL = 'https://clever.com/oauth/authorize'
/** SPA route of the callback page — under /account so it reaches the SPA like /account/signin does */
export const CLEVER_CALLBACK_PATH = '/account/clever/callback'

/**
 * Clever app client ID baked at build time (`REACT_APP_CLEVER_CLIENT_ID`,
 * same mechanism as the Mixpanel/OpenReplay tokens). `undefined` when the
 * environment has no Clever app, which hides the button.
 */
export function cleverClientId(): string | undefined {
  const raw = (env as Record<string, string | undefined>)['process.env.REACT_APP_CLEVER_CLIENT_ID']
  // env.json keeps the JSON.stringify quotes around every value
  const value = raw?.replace(/"/g, '').trim()
  return value || undefined
}

/** Absolute callback URL for the current origin */
export function cleverRedirectUri(): string {
  return `${window.location.origin}${CLEVER_CALLBACK_PATH}`
}

/** Clever authorization URL the "Log in with Clever" button navigates to */
export function cleverAuthorizeUrl(clientId: string): string {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: cleverRedirectUri(),
  })
  return `${CLEVER_AUTHORIZE_URL}?${params.toString()}`
}

/** Mirrors the backend status contract: 401 / 404 / 422 / anything else */
export type CleverSigninFailure = 'invalid_code' | 'not_provisioned' | 'invalid_request' | 'unknown'

export type CleverSigninResult = { success: true; user: IUser } | { success: false; reason: CleverSigninFailure }

/** Body of POST /api/arc_mosaico/auth/clever_signin on success */
interface CleverSigninResponse {
  user: {
    id: string
    email: string
    first_name: string
    last_name: string
    username?: string | null
    locale?: string | null
    selected_school_id?: string | null
    uid?: string | null
  }
  message: string
}

/** Same `user` localStorage shape every other login flow stores (IUser) */
export const toAuthUser = (user: CleverSigninResponse['user']): IUser => ({
  id: user.id,
  email: user.email,
  firstName: user.first_name,
  lastName: user.last_name,
  username: user.username || user.email,
  locale: user.locale || 'en',
  selectedSchoolId: user.selected_school_id || '',
  uid: user.uid || user.id,
})

const failureFor = (status?: number): CleverSigninFailure => {
  switch (status) {
    case 401:
      return 'invalid_code'
    case 404:
      return 'not_provisioned'
    case 422:
      return 'invalid_request'
    default:
      return 'unknown'
  }
}

/**
 * Exchanges the Clever `code` for a session. Plain axios (not the `api`
 * helper) on purpose: the helper swallows 401s and camelizes the body, and
 * this call needs the raw status to tell the user what went wrong.
 */
export async function cleverSignin(code: string): Promise<CleverSigninResult> {
  try {
    const response = await axios.post<CleverSigninResponse>(
      ARC_MOSAICO_ENDPOINTS.auth.cleverSignin,
      { code, redirect_uri: cleverRedirectUri() },
      { withCredentials: true },
    )
    return { success: true, user: toAuthUser(response.data.user) }
  } catch (error) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined
    return { success: false, reason: failureFor(status) }
  }
}

/**
 * Where to land after login: the page ProtectedRoute stored before sending
 * the visitor to sign in, unless it is itself an auth screen. Consumes the
 * cookie (same behaviour as the email confirmation flow).
 */
export function consumePostLoginUrl(): string {
  const stored = Cookies.get('url_to_redirect')
  Cookies.remove('url_to_redirect')
  if (!stored || stored.includes('/account/')) return '/'
  return stored
}
